"use client";

import { ImageThumbnail } from "@cmp-src/data-display/ImageThumbnail";
import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import { ImageOptionPreviewDialog } from "@cmp-src/form/ImageOptionPicker/ImageOptionPreviewDialog";
import { Button } from "@cmp-src/primitives/button";
import {
  isWorkplaceRequestReservationFieldValue,
  WorkplaceRequestElement,
  WorkplaceRequestReservationRunResult,
  WorkplaceRequestTypeField,
  WorkplaceRequestTypeFieldOption,
  WorkplaceRequestUser,
  WorkplaceRequestWorkflow
} from "@gettactic/api";
import { time24To12 } from "@gettactic/helpers/src/date-time/timeStringConverters";
import {
  getFileExtension,
  isImageFile
} from "@gettactic/helpers/src/files/fileType";
import { isSubmittableFieldType } from "@gettactic/helpers/src/workplace-requests/fieldTraits";
import {
  FileArchive,
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  Maximize2,
  Pencil
} from "lucide-react";
import React from "react";
import { RichTextBlock } from "@/features/workplace-requests/components/RichTextBlock";
import { bookedReservationOf } from "@/features/workplace-requests/lib/bookedReservation";
import { ReservationValueCard } from "./ReservationValueCard";
import { classes } from "./utils";

interface RequestFieldValuesProps {
  request: WorkplaceRequestElement;
  /** The type's full field list, so empty fields still render as "Not provided". */
  fields?: WorkplaceRequestTypeField[];
  /**
   * The type's workflows: they say which reservation question each booking
   * rule reads, so an approved request can show what was booked from it.
   */
  workflows?: WorkplaceRequestWorkflow[];
  /** Opens the edit modal. When provided (and the viewer can edit), an Edit button is shown. */
  onEdit?: () => void;
}

const NotProvided: React.FC = () => (
  <span className="text-neutral-400 italic">Not provided</span>
);

/** A lucide icon representing the file type, or null to fall back to the extension label. */
const getFileTypeIcon = (
  extension: string
): React.ComponentType<{ className?: string }> | null => {
  if (["pdf", "doc", "docx", "txt", "rtf", "md"].includes(extension)) {
    return FileText;
  }
  if (["xls", "xlsx", "csv"].includes(extension)) {
    return FileSpreadsheet;
  }
  if (["zip", "rar", "7z", "tar", "gz"].includes(extension)) {
    return FileArchive;
  }
  return null;
};

/**
 * Renders a `file_upload` value as a fixed 84×84 tile — an image thumbnail for
 * images, otherwise a type icon (or the uppercase extension as a last resort) —
 * with the filename ellipsed below it. Links to the file when a URL is present.
 */
const FileUploadValue: React.FC<{ url?: string; filename?: string }> = ({
  url,
  filename
}) => {
  const extension = getFileExtension(filename, url);
  const isImage = isImageFile(filename, url);
  const TypeIcon = getFileTypeIcon(extension);
  const label = filename || "Download file";

  const content = (
    <div className="flex w-[84px] flex-col gap-1.5">
      <div className="flex h-[84px] w-[84px] items-center justify-center overflow-hidden rounded-md border border-neutral-200 bg-neutral-100">
        {isImage && url ? (
          // biome-ignore lint/performance/noImgElement: signed thumbnail URL, avoids next/image host config
          <img src={url} alt="" className="h-full w-full object-cover" />
        ) : TypeIcon ? (
          <TypeIcon className="h-7 w-7 text-neutral-500" />
        ) : extension ? (
          <span className="font-semibold text-neutral-500 text-sm uppercase">
            {extension}
          </span>
        ) : (
          <FileIcon className="h-7 w-7 text-neutral-500" />
        )}
      </div>
      <span className="w-full truncate text-neutral-900 text-xs group-hover:text-neutral-600">
        {label}
      </span>
    </div>
  );

  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="group inline-block"
      >
        {content}
      </a>
    );
  }

  return <div className="inline-block">{content}</div>;
};

/**
 * A picked option whose choice is visual: the image is shown large enough to
 * read at a glance and opens full size on click, mirroring the picker's own
 * rows. An inline thumbnail is too small to tell layouts apart, which used to
 * leave managers opening the edit form just to see what was requested.
 */
const SelectedImageOption: React.FC<{
  option: WorkplaceRequestTypeFieldOption;
}> = ({ option }) => {
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);
  const previewOption = {
    id: option.id,
    label: option.label,
    description: option.description,
    imageUrl: option.image_url
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsPreviewOpen(true)}
        aria-label={`Preview the image for ${option.label}`}
        className="group flex w-28 flex-col gap-1.5 text-left focus-visible:outline-none"
      >
        <span className="relative">
          <ImageThumbnail
            src={option.image_url}
            size="lg"
            className="w-full group-focus-visible:ring-2 group-focus-visible:ring-brand"
          />
          <span className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-md bg-white/90 text-neutral-700 shadow-sm transition-colors group-hover:bg-white group-hover:text-neutral-900">
            <Maximize2 className="h-3.5 w-3.5" />
          </span>
        </span>
        <span className="w-full truncate text-neutral-900 text-xs group-hover:text-neutral-600">
          {option.label}
        </span>
      </button>
      <ImageOptionPreviewDialog
        option={isPreviewOpen ? previewOption : null}
        onClose={() => setIsPreviewOpen(false)}
      />
    </>
  );
};

/** A picked select option: its optional image next to the label. */
const SelectedOption: React.FC<{ option: WorkplaceRequestTypeFieldOption }> = ({
  option
}) =>
  option.image_url ? (
    <SelectedImageOption option={option} />
  ) : (
    <span className="inline-flex items-center gap-1.5">
      <ImageThumbnail src={option.image_url} size="xs" />
      {option.label}
    </span>
  );

/**
 * A `person` answer: the stored user id comes back from the API resolved into
 * the full user, so it is shown like every other user in the modal.
 */
const PersonValue: React.FC<{ user: WorkplaceRequestUser }> = ({ user }) => (
  <span className="inline-flex items-center gap-2">
    <UserAvatar
      user={{
        name: user.name || "",
        email: user.email || "",
        profile_photo_url: user.profile_photo_url || ""
      }}
      size="xs"
      className="flex-shrink-0"
    />
    <span className="truncate">{user.name || user.email}</span>
  </span>
);

/**
 * A `reservation` answer, as its own block rather than a line of text.
 *
 * Nothing here is booked yet — the reservations are created when the request
 * is approved — so it reads as what was asked for, and says so underneath. The
 * zone is named because the times are the office's wall-clock, not the
 * reader's: an approver two zones away would otherwise read them as their own.
 */
const renderValue = (
  field: WorkplaceRequestTypeField,
  value: unknown,
  /** The request's office: a reservation's times are that office's wall-clock. */
  officeId: string,
  /** What the approval booked, once it has — see {@link bookedReservationOf}. */
  booked: WorkplaceRequestReservationRunResult | null
): React.ReactNode => {
  if (field.field_type === "toggle") {
    return value === true ? "Yes" : "No";
  }

  const isEmpty =
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);
  if (isEmpty) {
    return <NotProvided />;
  }

  switch (field.field_type) {
    case "single_select": {
      const option = field.options?.find((o) => o.id === value);
      if (!option) return String(value);
      return <SelectedOption option={option} />;
    }
    case "multi_select": {
      const ids = Array.isArray(value) ? (value as string[]) : [];
      return (
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {ids.map((id) => {
            const option = field.options?.find((o) => o.id === id);
            return option ? (
              <SelectedOption key={id} option={option} />
            ) : (
              <span key={id}>{id}</span>
            );
          })}
        </span>
      );
    }
    case "file_upload": {
      const file = value as { url?: string; filename?: string };
      return <FileUploadValue url={file?.url} filename={file?.filename} />;
    }
    case "person": {
      if (typeof value !== "object") return String(value);
      return <PersonValue user={value as WorkplaceRequestUser} />;
    }
    case "reservation": {
      if (!isWorkplaceRequestReservationFieldValue(value)) {
        return <NotProvided />;
      }
      return (
        <ReservationValueCard
          value={value}
          officeId={officeId}
          booked={booked}
        />
      );
    }
    // Core stores 24-hour; show it back the way the submitter picked it.
    case "time":
      return typeof value === "string" ? time24To12(value) : String(value);
    // HTML from the rich-text editor, or a plain string from before it was.
    case "long_text":
      return (
        <RichTextBlock html={String(value)} className="text-neutral-900" />
      );
    default:
      return String(value);
  }
};

/**
 * Renders a ticket's submitted form answers as label/value rows, grouped under the
 * request type's name. Uses the type's full field list (when available) so unanswered
 * fields still appear as "Not provided".
 */
export const RequestFieldValues: React.FC<RequestFieldValuesProps> = ({
  request,
  fields,
  workflows,
  onEdit
}) => {
  const valueByFieldId = new Map(
    (request.field_values ?? []).map((fv) => [fv.id, fv.value])
  );

  // Prefer the type's full field list; fall back to whatever values are present.
  // Info blocks are filtered out either way: they are content the submitter
  // read, not an answer they gave, so they belong nowhere in this list — least
  // of all as a row reading "Not provided".
  const rows = (
    (fields && fields.length > 0 ? fields : request.field_values) ?? []
  ).filter((row) => isSubmittableFieldType(row.field_type));

  // Editability is resolved by the backend per-request (it accounts for
  // category membership, not just org-wide admin permissions).
  const canEdit =
    Boolean(request.capabilities?.can_edit_field_values) && !!onEdit;
  // Keep the section (and its Edit button) even with no custom fields, so admins
  // still have the edit affordance here rather than in the header.
  if (rows.length === 0 && !canEdit) {
    return null;
  }

  return (
    <section className="mt-8">
      <div className="mb-1 flex items-center justify-between gap-2">
        <h3 className={classes.sectionHeading}>Request Details</h3>
        {canEdit ? (
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<Pencil className="h-3.5 w-3.5" />}
            onClick={onEdit}
            className="rounded-brand border border-neutral-200 text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700"
          >
            Edit
          </Button>
        ) : null}
      </div>
      {rows.length > 0 ? (
        <dl className="flex flex-col">
          {rows.map((field) => (
            <div
              key={field.id}
              className="flex flex-col gap-1 border-separator border-t py-3 first:border-t-0"
            >
              <dt className="text-[12.5px] text-neutral-600 leading-[17.5px]">
                {field.name}
              </dt>
              <dd className="text-neutral-900 text-sm">
                {renderValue(
                  field,
                  valueByFieldId.get(field.id),
                  request.office_id,
                  bookedReservationOf(request, field.id, workflows)
                )}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="py-3 text-neutral-400 text-sm">No additional details.</p>
      )}
    </section>
  );
};
